import React, { useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { UserSkinProfile } from '../../types';
import { useAppContext } from '../../context/AppContext';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);


type TimePeriod = '6months' | '1year';

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
    });
};

interface SkinHealthChartProps {
    profileData: UserSkinProfile[];
    clickedUserId?: string;
}

const SkinHealthChart: React.FC<SkinHealthChartProps> = ({ profileData, clickedUserId }) => {
    const { getTranslation } = useAppContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.down('md'));
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('6months');

    /** Same as MultiChart: dropdown menu list without extra left padding from global ul rules */
    const selectMenuProps = {
        MenuListProps: {
            sx: { paddingLeft: '0 !important' },
        },
    };

    const processedData = useMemo(() => {
        // Filter data based on clickedUserId if provided
        let filteredProfileData = profileData;
        if (clickedUserId) {
            // Find the clicked user's data and show only their sessions
            const clickedUser = profileData.find(profile => profile._id === clickedUserId);
            if (clickedUser) {
                // For anonymous users, show only their single session
                // For registered users, show all their sessions (filter by user_id if available)
                filteredProfileData = profileData.filter(profile =>
                    profile._id === clickedUserId ||
                    (profile.user_id && clickedUser.user_id && profile.user_id === clickedUser.user_id)
                );
            }
        }

        const sortedProfiles = [...filteredProfileData].sort((a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        const processedProfiles = sortedProfiles.map(profile => ({
            date: formatDate(profile.createdAt),
            score: profile.output_score.find(s => s.concern === 'skin_health')?.score || 0
        }));

        const currentDate = new Date();
        const filteredProfiles = processedProfiles.filter(profile => {
            const profileDate = new Date(profile.date);
            const monthsDiff = (currentDate.getFullYear() - profileDate.getFullYear()) * 12 +
                (currentDate.getMonth() - profileDate.getMonth());

            switch (timePeriod) {
                case '6months': return monthsDiff <= 6;
                case '1year': return monthsDiff <= 12;
            }
        });

        return {
            labels: filteredProfiles.map(p => p.date),
            scores: filteredProfiles.map(p => p.score)
        };
    }, [profileData, timePeriod, clickedUserId]);

    const scoreLabel = useMemo(() => {
        return getTranslation("Txt612");
    }, [getTranslation]);

    const options = useMemo(() => {
        const label = scoreLabel;
        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    min: 0,
                    max: 100,
                    ticks: {
                        stepSize: 20
                    },
                    grid: {
                        display: false
                    }
                },
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        maxRotation: 45,
                        autoSkip: true,
                        maxTicksLimit: 6
                    }
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#000',
                    bodyColor: '#666',
                    borderColor: '#e0e0e0',
                    borderWidth: 1,
                    displayColors: false,
                    callbacks: {
                        label: (context: any) => {
                            return `${label}: ${context.parsed.y}`;
                        }
                    }
                }
            },
            elements: {
                line: {
                    tension: 0,
                    borderWidth: 2,
                    borderColor: '#62B2FD',
                    fill: false
                },
                point: {
                    radius: 3,
                    hoverRadius: 6,
                    backgroundColor: 'white',
                    borderColor: '#62B2FD',
                    borderWidth: 2
                }
            }
        };
    }, [scoreLabel]);

    const data = {
        labels: processedData.labels,
        datasets: [
            {
                data: processedData.scores,
                borderColor: '#62B2FD',
                backgroundColor: 'rgba(98, 178, 253, 0.5)',
            }
        ]
    };

    return (
        <Card sx={{
            boxShadow: 3,
            borderRadius: 2,
            margin: '0 auto',
            width: "100%"
        }}>
            <CardContent sx={{
                padding: isMobile ? 1.5 : 2
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    mb: isMobile ? 1.5 : 2,
                    gap: isMobile ? 1 : 0
                }}>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="div" sx={{
                        fontWeight: 'medium',
                        fontSize: isMobile ? '14px' : '1.25rem',
                        mb: isMobile ? 1 : 0
                    }}>
                        {getTranslation("Txt306")}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: isMobile ? 'flex-end' : '',
                        gap: 1,
                        width: isMobile ? '100%' : 'auto',
                    }}>
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: isMobile ? '40%' : 120,
                                alignSelf: isMobile ? 'stretch' : 'auto'
                            }}
                        >
                            <Select
                                value={timePeriod}
                                onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                                displayEmpty
                                MenuProps={selectMenuProps}
                                sx={{
                                    borderRadius: 1,
                                    fontSize: isMobile ? '0.875rem' : '1rem'
                                }}
                            >
                                <MenuItem value="6months">{getTranslation("Txt536")}</MenuItem>
                                <MenuItem value="1year">{getTranslation("Txt889")}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={{
                    height: isMobile ? 400 : (isMedium ? 500 : 600),
                    position: 'relative',
                    width: '100%'
                }}>
                    <Line options={options} data={data} />
                </Box>
            </CardContent>
        </Card>
    );
};

export default SkinHealthChart;
